/**
 * Resolve the designer's image intents to real photographs.
 *
 * This is the deterministic half of the image flow. The model has already
 * decided what imagery the design needs; everything here is lookup and
 * attachment. It may NOT decide composition, scale, crop or subject — if this
 * module ever starts choosing what a page should show, the design engine has
 * quietly moved out of the model and into a search box.
 *
 * The provider key lives on the server. Requests go through the same
 * admin-authenticated proxy the design generation uses, so no image API key
 * exists in the frontend and none ever will.
 *
 * On a miss the intent stays unresolved. There is no synthetic fallback and no
 * invented URL: a seeded placeholder service answers every query with a real
 * photograph of the wrong thing, which reads as success to every check
 * downstream while the flyer shows a stranger's living room.
 */

import { ADMIN_TOKEN_KEY } from '../openai/openaiSchema';
import { collectAssetIntents, refinementQueries } from './assetIntent';

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

export const RESOLVE_ENDPOINT = '/api/admin/ai-generator/resolve-images';

/** Re-searches allowed per unresolved intent, after the first attempt. */
export const DEFAULT_MAX_REFINEMENTS = 2;

/** Thrown when the resolver cannot reach the image service at all. */
export class AssetResolutionError extends Error {
  constructor(message, { status = null } = {}) {
    super(message);
    this.name = 'AssetResolutionError';
    this.status = status;
  }
}

const readToken = () => {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(ADMIN_TOKEN_KEY)) || '';
  } catch {
    return '';
  }
};

/**
 * One round trip. Returns the raw results array from the proxy.
 * @returns {Promise<{results: Array, providers: string[]}>}
 */
export const requestResolution = async (intents, config = {}) => {
  const {
    fetchImpl,
    apiUrl = process.env.REACT_APP_BACKEND_URL || '',
    endpoint = RESOLVE_ENDPOINT,
  } = config;
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch) throw new AssetResolutionError('No fetch implementation is available.');

  let res;
  try {
    res = await doFetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readToken()}` },
      body: JSON.stringify({ intents }),
    });
  } catch (err) {
    throw new AssetResolutionError(`Could not reach the image service: ${err && err.message}`);
  }

  if (!res.ok) {
    let detail = `Image resolution failed (${res.status})`;
    try { detail = (await res.json()).detail || detail; } catch { /* keep the status message */ }
    throw new AssetResolutionError(detail, { status: res.status });
  }
  const payload = await res.json();
  return { results: asArray(payload.results), providers: asArray(payload.providers) };
};

/**
 * The editor's own frame-image shape.
 *
 * A frame does not take a URL string. CanvasEditor and PageThumbnailPreview
 * both read `el.image.src` and hand `naturalWidth`/`naturalHeight` to
 * computeFrameImageLayout to size the crop. Assigning the URL directly to
 * `el.image` type-checks as "truthy, so there is an image", renders
 * `<img src={undefined}>`, and paints a blank box — which is exactly what the
 * browser showed after the photographs were resolving correctly.
 */
const frameImage = (asset) => ({
  src: asset.url,
  naturalWidth: asset.width || 0,
  naturalHeight: asset.height || 0,
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
});

/**
 * Attach resolved assets to the rendered pages. Pure — returns new pages.
 *
 * The two element kinds carry an image differently: an `image` element paints
 * from `src`, a `frame` element from the `image` OBJECT above. The renderer
 * maps a V2 image node to a frame element, so both are set.
 */
export const attachResolvedAssets = (pages, byId) => asArray(pages).map((page) => ({
  ...page,
  elements: asArray(asObject(page).elements).map((el) => {
    const hit = byId[el && el.id];
    if (!hit || !hit.asset || !hit.asset.url) return el;
    return {
      ...el,
      src: hit.asset.url,
      ...(el.type === 'frame' ? { image: frameImage(hit.asset) } : {}),
      designV2: {
        ...asObject(el.designV2),
        resolvedAsset: {
          url: hit.asset.url,
          provider: hit.asset.provider,
          credit: hit.asset.credit,
          width: hit.asset.width,
          height: hit.asset.height,
          query: hit.query || null,
        },
      },
    };
  }),
}));

/**
 * Resolve every image intent in `pages`, with bounded re-search.
 *
 * @param {Array} pages   rendered pages
 * @param {Object} [config]  { fetchImpl, apiUrl, endpoint, maxRefinements }
 * @returns {Promise<{ pages: Array, resolved: number, unresolved: Array, providers: string[] }>}
 *          `unresolved` carries `{ id, role, required, reason, triedQueries }` —
 *          a controlled failure the art director can act on, never a fake URL.
 */
export const resolveAssetIntents = async (pages, config = {}) => {
  const { maxRefinements = DEFAULT_MAX_REFINEMENTS } = config;
  const wanted = collectAssetIntents(pages);
  if (!wanted.length) return { pages, resolved: 0, unresolved: [], providers: [] };

  const byId = {};
  const tried = {};
  let providers = [];

  // Attempt 0 is the model's own query; each later pass re-searches only the
  // intents still missing, with the next broader phrasing of the model's terms.
  let pending = wanted;
  for (let pass = 0; pass <= maxRefinements && pending.length; pass += 1) {
    const batch = [];
    pending.forEach(({ id, intent }) => {
      // The next phrasing this intent has not already been searched with.
      // An intent whose variants are exhausted simply stops being retried,
      // rather than spending another round trip on the same query.
      const next = refinementQueries(intent).find((q) => !(tried[id] || []).includes(q));
      if (!next) return;
      tried[id] = [...(tried[id] || []), next];
      batch.push({ ...intent, id, searchQuery: next });
    });

    if (!batch.length) break;

    const { results, providers: got } = await requestResolution(batch, config);
    if (got.length) providers = got;

    results.forEach((r) => { if (r && r.resolved && r.asset) byId[r.id] = r; });
    // Drop only what RESOLVED. Filtering by "was reported missing" instead lost
    // any intent whose phrasings were exhausted before this pass: it was absent
    // from the batch, so absent from the results, so absent from the missing
    // set — and it fell out of `pending` without ever being reported. A required
    // hero could disappear from `unresolved` entirely, and the blocker that
    // exists to catch exactly that would never fire.
    pending = pending.filter(({ id }) => !byId[id]);
  }

  const unresolved = pending.map(({ id, intent }) => ({
    id,
    role: intent.role,
    required: intent.required,
    reason: 'no-match',
    triedQueries: tried[id] || [],
  }));

  return {
    pages: attachResolvedAssets(pages, byId),
    resolved: Object.keys(byId).length,
    unresolved,
    providers,
  };
};
